import { useEffect, useRef, useState, useCallback } from "react";
import { logger } from "@/lib/logger";
import { wsUrl } from "@/lib/api";

const RECONNECT_MS = 3000;
const PING_MS = 5000;

// Connects to backend WebSocket and invokes onMessage for every broadcast.
// `enabled` gates the connection so we only open the socket once auth has resolved
// (avoids a transient "closed before connection established" warning on first paint).
export function useRealtime(onMessage, enabled = true) {
  const [connected, setConnected] = useState(false);
  const [latency, setLatency] = useState(null);
  const wsRef = useRef(null);
  const pingSent = useRef(0);
  const cbRef = useRef(onMessage);
  const connectRef = useRef(null); // latest connect(), used by the reconnect timer
  cbRef.current = onMessage;

  const handleMessage = useCallback((evt) => {
    try {
      const msg = JSON.parse(evt.data);
      if (msg.type === "pong") {
        setLatency(Date.now() - pingSent.current);
        return;
      }
      if (cbRef.current) cbRef.current(msg);
    } catch (e) {
      logger.error("Failed to process realtime message:", e);
    }
  }, [cbRef, pingSent, setLatency]);

  const connect = useCallback(() => {
    let ws;
    try {
      ws = new WebSocket(wsUrl());
    } catch (e) {
      logger.error("WebSocket connection failed:", e);
      return;
    }
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => {
      setConnected(false);
      setTimeout(() => connectRef.current?.(), RECONNECT_MS);
    };
    ws.onerror = () => ws.close();
    ws.onmessage = handleMessage;
  }, [wsRef, connectRef, handleMessage, setConnected]);
  connectRef.current = connect;

  useEffect(() => {
    if (!enabled) return undefined;
    connect();
    const interval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        pingSent.current = Date.now();
        wsRef.current.send("ping");
      }
    }, PING_MS);
    return () => {
      clearInterval(interval);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [connect, enabled, wsRef, pingSent]);

  const send = useCallback((obj) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(obj));
    }
  }, [wsRef]);

  return { connected, latency, send };
}
